import { ClickAwayListener } from "@mui/base";
import type { PopperPlacementType } from "@mui/base/Popper";
import {
  type JSXElementConstructor,
  type KeyboardEvent,
  memo,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dropdown } from "../../dropdown";
import { Flex } from "../../flex";
import {
  type RedoButtonSize,
  type RedoButtonTheme,
} from "../buttons/redo-button";
import { RedoCommandMenuItemComponent } from "./redo-command-menu-item";
import { RedoCommandMenuSubmenuItem } from "./redo-command-menu-submenu-item";
import * as redoCommandMenuCss from "./redo-command-menu.module.css";

export interface RedoCommandMenuItem {
  Icon?: JSXElementConstructor<any>;
  text: string;
  onClick?(event: MouseEvent<HTMLButtonElement>): void;
  dataTestId?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  tooltip?: string;
  theme?: RedoButtonTheme;
  size?: RedoButtonSize;
  show?: boolean;
  id?: string;
  shortcutHotkey?: (string | number)[];
  TrailingIcon?: JSXElementConstructor<any>;
  children?: RedoCommandMenuItem[];
  customSubmenuContent?: ReactNode;
  submenuPlacement?: PopperPlacementType;
}

export interface RedoCommandMenuSeparator {
  readonly _type: "separator";
}

export const REDO_COMMAND_MENU_SEPARATOR: RedoCommandMenuSeparator = {
  _type: "separator",
} as const;

export type RedoCommandMenuEntry =
  | RedoCommandMenuItem
  | RedoCommandMenuSeparator;

export function isSeparator(
  entry: RedoCommandMenuEntry,
): entry is RedoCommandMenuSeparator {
  return "_type" in entry && entry._type === "separator";
}

function isSubmenuItem(item: RedoCommandMenuItem): boolean {
  return item.children !== undefined || item.customSubmenuContent !== undefined;
}

export const RedoCommandMenu = memo(function RedoCommandMenu({
  dontRestrainIconWidth,
  items,
  open,
  setOpen,
  anchor,
  size = "sm",
  fitToAnchor = false,
  placement,
  dropdownClassName,
}: {
  dontRestrainIconWidth?: boolean;
  items: RedoCommandMenuEntry[];
  open: boolean;
  setOpen(value: boolean): void;
  anchor: HTMLElement | null;
  size?: RedoButtonSize;
  fitToAnchor?: boolean;
  placement?: PopperPlacementType;
  dropdownClassName?: string;
}) {
  const atLeastOneIcon = items.some(
    (entry) => !isSeparator(entry) && entry.Icon !== undefined,
  );
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState<number | null>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const entriesToShow = useMemo(
    () => items.filter((entry) => isSeparator(entry) || entry.show !== false),
    [items],
  );

  // Clean up stale refs when items change
  buttonRefs.current.length = entriesToShow.length;

  // Close submenus when menu closes
  useEffect(() => {
    if (!open) {
      setOpenSubmenuIndex(null);
    }
  }, [open]);

  // Focus first non-disabled item when menu opens
  useEffect(() => {
    if (open) {
      // Delay needed because dropdown renders via portal
      requestAnimationFrame(() => {
        const firstEnabledIndex = entriesToShow.findIndex(
          (entry) => !isSeparator(entry) && !entry.disabled,
        );
        if (firstEnabledIndex >= 0) {
          setFocusedIndex(firstEnabledIndex);
          buttonRefs.current[firstEnabledIndex]?.focus({ preventScroll: true });
        }
      });
    }
  }, [open, entriesToShow]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // When a submenu is open, let the submenu handle ArrowUp/ArrowDown
      if (openSubmenuIndex !== null) {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          return;
        }
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          e.stopPropagation();
          setOpenSubmenuIndex(null);
          buttonRefs.current[openSubmenuIndex]?.focus();
          return;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          setOpenSubmenuIndex(null);
          buttonRefs.current[openSubmenuIndex]?.focus();
          return;
        }
      }

      const enabledIndices = entriesToShow
        .map((entry, idx) =>
          !isSeparator(entry) && !entry.disabled ? idx : -1,
        )
        .filter((idx) => idx !== -1);

      if (enabledIndices.length === 0) {
        return;
      }

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          const currentPos = enabledIndices.indexOf(focusedIndex);
          const nextPos =
            currentPos === -1 ? 0 : (currentPos + 1) % enabledIndices.length;
          const nextIndex = enabledIndices[nextPos];
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          setFocusedIndex(nextIndex);
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          buttonRefs.current[nextIndex]?.focus();
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          const currentPos = enabledIndices.indexOf(focusedIndex);
          const prevPos =
            currentPos === -1
              ? 0
              : (currentPos - 1 + enabledIndices.length) %
                enabledIndices.length;
          const prevIndex = enabledIndices[prevPos];
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          setFocusedIndex(prevIndex);
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          buttonRefs.current[prevIndex]?.focus();
          break;
        }
        case "ArrowRight": {
          const focusedEntry = entriesToShow[focusedIndex];
          if (
            focusedEntry &&
            !isSeparator(focusedEntry) &&
            isSubmenuItem(focusedEntry)
          ) {
            e.preventDefault();
            setOpenSubmenuIndex(focusedIndex);
          }
          break;
        }
        case "Escape": {
          e.preventDefault();
          setOpen(false);
          anchor?.focus();
          break;
        }
      }
    },
    [
      entriesToShow,
      focusedIndex,
      setOpen,
      anchor,
      openSubmenuIndex,
      setOpenSubmenuIndex,
    ],
  );

  const dropdown = (
    <Dropdown
      anchor={anchor}
      className={dropdownClassName}
      fitToAnchor={fitToAnchor}
      open={open}
      placement={placement}
    >
      <div onKeyDown={handleKeyDown} role="menu">
        <Flex dir="column" gap="xs">
          {entriesToShow.map((entry, idx) => {
            if (isSeparator(entry)) {
              return (
                <div
                  className={redoCommandMenuCss.separator}
                  key={`separator-${idx}`}
                  role="separator"
                />
              );
            }

            if (isSubmenuItem(entry)) {
              return (
                <RedoCommandMenuSubmenuItem
                  buttonRef={(el) => {
                    buttonRefs.current[idx] = el;
                  }}
                  closeMenu={() => setOpen(false)}
                  dontRestrainIconWidth={dontRestrainIconWidth}
                  fitToAnchor={fitToAnchor}
                  indentForIcon={atLeastOneIcon}
                  isOpen={openSubmenuIndex === idx}
                  item={entry}
                  key={entry.id ?? idx}
                  onClose={() => {
                    setOpenSubmenuIndex(null);
                    buttonRefs.current[idx]?.focus();
                  }}
                  onOpen={() => setOpenSubmenuIndex(idx)}
                  size={size}
                />
              );
            }

            return (
              <RedoCommandMenuItemComponent
                dontRestrainIconWidth={dontRestrainIconWidth}
                fitToAnchor={fitToAnchor}
                indentForIcon={atLeastOneIcon}
                item={entry}
                itemClicked={() => setOpen(false)}
                key={entry.id ?? idx}
                ref={(el) => {
                  buttonRefs.current[idx] = el;
                }}
                size={size}
              />
            );
          })}
        </Flex>
      </div>
    </Dropdown>
  );

  return (
    <>
      {open && (
        <ClickAwayListener
          onClickAway={() => {
            setOpen(false);
          }}
        >
          {dropdown}
        </ClickAwayListener>
      )}
    </>
  );
});
